import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

export interface FileUploadStatus {
  id: string;
  fileName: string;
  status: 'uploading' | 'uploaded' | 'error';
  errorMessage?: string;
}

export interface UseDnDFileUploadOptions {
  initialValue?: string;
  onUpload?: (url: string, file: File) => void;
  externalError?: string;
  externalLoading?: boolean;
  disabled?: boolean;
  acceptedTypes?: string[];
  onFileSelect?: (file: File) => void;
  maxFiles?: number;
  currentFilesCount?: number;
}

export interface UseDnDFileUploadReturn {
  // State
  value: string;
  isError: string;
  isLoading: boolean;
  isDragOver: boolean;

  // Refs
  inputRef: React.RefObject<HTMLInputElement | null>;

  // Handlers
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragEnter: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileError: () => void;
  handleFileLoad: () => void;
  handleClick: () => void;

  // Utils
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

const MAX_FILE_SIZE_DEFAULT = 10 * 1024 * 1024; // 10MB
const MAX_FILE_SIZE_LARGE = 50 * 1024 * 1024; // 50MB for .docx and .pdf

// File types that support larger file sizes (50MB)
const LARGE_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
];

// Get max file size based on file type
const getMaxFileSize = (fileType: string): number => {
  return LARGE_FILE_TYPES.includes(fileType)
    ? MAX_FILE_SIZE_LARGE
    : MAX_FILE_SIZE_DEFAULT;
};

// Format file size for display
const formatFileSize = (bytes: number): string => {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(0)} MB`
    : `${(bytes / 1024).toFixed(0)} KB`;
};

export function useDnDFileUpload({
  initialValue = '',
  externalError,
  externalLoading,
  disabled = false,
  acceptedTypes = [],
  onFileSelect,
  maxFiles = Infinity,
  currentFilesCount = 0,
}: UseDnDFileUploadOptions = {}): UseDnDFileUploadReturn {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(initialValue);
  const [isError, setIsError] = useState(externalError || '');
  const [isLoading, setIsLoading] = useState(!!initialValue);
  const [isDragOver, setIsDragOver] = useState(false);

  // Sync external error with internal state
  useEffect(() => {
    if (externalError) {
      setIsError(externalError);
    } else {
      setIsError('');
    }
  }, [externalError, value]);

  // Sync external loading with internal state
  useEffect(() => {
    if (externalLoading !== undefined) {
      setIsLoading(externalLoading);
    }
  }, [externalLoading]);

  // Sync initial value changes
  useEffect(() => {
    setValue(initialValue);
    if (initialValue) {
      setIsLoading(true);
    }
  }, [initialValue]);

  const handleFileError = () => {
    setIsLoading(false);
    setIsError('Failed to load file');
  };

  const handleFileLoad = () => {
    setIsLoading(false);
  };

  const isFileTypeAccepted = (file: File): boolean => {
    if (acceptedTypes.length === 0) return true;
    return acceptedTypes.some((type) => file.type.startsWith(type));
  };

  const handleFileChange = async (file?: File, needClear?: boolean) => {
    if (!file) {
      return;
    }

    // Check file type first
    if (!isFileTypeAccepted(file)) {
      toast.error(
        `Invalid file type: ${
          file.name.split('.').pop()?.toLowerCase() ?? 'unknown'
        } is not supported.`,
      );
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }

    // Check file size based on file type
    const maxSize = getMaxFileSize(file.type);
    if (file.size > maxSize) {
      toast.error(
        `File size exceeds the maximum allowed size of ${formatFileSize(
          maxSize,
        )}`,
      );
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }

    // Check max files limit
    if (currentFilesCount >= maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }

    await onFileSelect?.(file);
    if (inputRef.current && needClear) {
      inputRef.current.value = '';
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    const availableSlots = maxFiles - currentFilesCount;
    const filesToProcess = Math.min(files.length, availableSlots);

    if (files.length > availableSlots) {
      toast.error(
        `Maximum ${maxFiles} files allowed. Only ${availableSlots} file(s) can be added.`,
      );
    }

    for (let i = 0; i < filesToProcess; i++) {
      handleFileChange(files[i], i === filesToProcess - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const files = e.target.files;
    if (!files) return;
    const availableSlots = maxFiles - currentFilesCount;
    const filesToProcess = Math.min(files.length, availableSlots);

    if (files.length > availableSlots) {
      toast.error(
        `Maximum ${maxFiles} files allowed. Only ${availableSlots} file(s) can be added.`,
      );
    }

    for (let i = 0; i < filesToProcess; i++) {
      handleFileChange(files[i], i === filesToProcess - 1);
    }
  };

  const handleClick = () => {
    if (disabled || isLoading) return;
    inputRef.current?.click();
  };

  const clearError = () => {
    setIsError('');
  };

  const setLoadingState = (loading: boolean) => {
    setIsLoading(loading);
  };

  return {
    // State
    value,
    isError,
    isLoading,
    isDragOver,

    // Refs
    inputRef,

    // Handlers
    handleDrop,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleInputChange,
    handleFileError,
    handleFileLoad,
    handleClick,

    // Utils
    clearError,
    setLoading: setLoadingState,
  };
}
